// Current-site context — the one place the console remembers WHICH site the
// operator is looking at. Mounted once in AppShell; every page reads it through
// `useSite()` and defaults its own asset selection to it.
//
// • `sites` comes from the shared `tenantAssets` document (urql's document
//   cache, so the header, the Sites table and the launcher all see one list and
//   every createAsset / updateAsset result invalidates it).
// • `currentSiteId` is a site id or "all". It is persisted in
//   localStorage["pb.site"]; when exactly one site exists it is auto-selected,
//   and a stored id that no longer exists falls back to "all".
// • The id is mirrored onto `<html data-site>` for CSS-free consumers (tests,
//   wall-mode scripts, analytics hooks) — the same trick density.ts uses.
// • The add / edit site drawer and the phone site-picker sheet are owned here
//   too, so the header, the launcher, /asset/sites and the tab bar all reuse
//   one instance (`openAddSite`, `openEditSite`, `openPicker`).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "urql";

import { TenantAssetsQuery } from "@/app/queries";
import type { SiteAsset } from "@/app/queriesSites";
import { useAuth } from "@/lib/auth/AuthProvider";
import { hasAnyRole } from "@/lib/auth/RequireRole";

export type SiteId = number | "all";

export const SITE_STORAGE_KEY = "pb.site";
/** Roles that may create or edit sites — mirrors the BFF's ensure_asset_write. */
export const SITE_MANAGER_ROLES: ReadonlyArray<string> = ["admin", "platform_admin", "ops_manager"];

export type SiteDrawerState = { kind: "closed" } | { kind: "create" } | { kind: "edit"; id: number };

export interface SiteContextValue {
  /** Active sites of the tenant, in id order (empty while loading). */
  sites: ReadonlyArray<SiteAsset>;
  /** True until the first `tenantAssets` result has arrived. */
  loading: boolean;
  error: string | null;
  currentSiteId: SiteId;
  /** The selected site, or null for "all" (and while loading). */
  currentSite: SiteAsset | null;
  setCurrentSite: (id: SiteId) => void;
  refetchSites: () => void;
  /** admin / platform_admin / ops_manager. */
  canManageSites: boolean;
  drawer: SiteDrawerState;
  openAddSite: () => void;
  openEditSite: (id: number) => void;
  closeDrawer: () => void;
  /** Phone bottom-sheet site picker (the header popover is hidden below `sm`). */
  pickerOpen: boolean;
  openPicker: () => void;
  closePicker: () => void;
}

const SiteContext = createContext<SiteContextValue | null>(null);

function readStored(): SiteId | null {
  try {
    const raw = localStorage.getItem(SITE_STORAGE_KEY);
    if (raw === null) return null;
    if (raw === "all") return "all";
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeStored(id: SiteId): void {
  try {
    localStorage.setItem(SITE_STORAGE_KEY, String(id));
  } catch {
    /* private mode / quota — the in-memory choice still applies */
  }
}

export function SiteProvider({ children }: { children: ReactNode }) {
  const { roles } = useAuth();
  const canManageSites = hasAnyRole(roles, [...SITE_MANAGER_ROLES]);

  const [{ data, fetching, error }, reexecute] = useQuery({ query: TenantAssetsQuery, requestPolicy: "cache-and-network" });
  const sites = useMemo<ReadonlyArray<SiteAsset>>(() => data?.tenantAssets ?? [], [data?.tenantAssets]);
  const loaded = data != null;

  // What the operator chose (or nothing yet). The effective id is derived so a
  // stale id never flashes as "current" before the fallback applies.
  const [stored, setStored] = useState<SiteId | null>(readStored);

  const currentSiteId = useMemo<SiteId>(() => {
    if (!loaded) return stored ?? "all";
    if (typeof stored === "number" && sites.some((s) => s.id === stored)) return stored;
    if (stored === "all") return "all";
    // Nothing stored, or the stored site disappeared: the only site, else all.
    return sites.length === 1 ? sites[0]!.id : "all";
  }, [loaded, stored, sites]);

  const currentSite = useMemo(() => (typeof currentSiteId === "number" ? sites.find((s) => s.id === currentSiteId) ?? null : null), [currentSiteId, sites]);

  // Keep storage honest once the list is known (clears a vanished id, records
  // the auto-selected only site).
  useEffect(() => {
    if (loaded && stored !== currentSiteId) {
      setStored(currentSiteId);
      writeStored(currentSiteId);
    }
  }, [loaded, stored, currentSiteId]);

  const setCurrentSite = useCallback((id: SiteId) => {
    setStored(id);
    writeStored(id);
  }, []);

  // <html data-site="38"> / data-site="all" for CSS-free consumers.
  useEffect(() => {
    const el = document.documentElement;
    el.dataset.site = String(currentSiteId);
    return () => {
      delete el.dataset.site;
    };
  }, [currentSiteId]);

  const refetchSites = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);

  const [drawer, setDrawer] = useState<SiteDrawerState>({ kind: "closed" });
  const openAddSite = useCallback(() => setDrawer({ kind: "create" }), []);
  const openEditSite = useCallback((id: number) => setDrawer({ kind: "edit", id }), []);
  const closeDrawer = useCallback(() => setDrawer({ kind: "closed" }), []);

  const [pickerOpen, setPickerOpen] = useState(false);
  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);

  const value = useMemo<SiteContextValue>(
    () => ({
      sites,
      loading: fetching && !loaded,
      error: error?.message ?? null,
      currentSiteId,
      currentSite,
      setCurrentSite,
      refetchSites,
      canManageSites,
      drawer,
      openAddSite,
      openEditSite,
      closeDrawer,
      pickerOpen,
      openPicker,
      closePicker,
    }),
    [sites, fetching, loaded, error, currentSiteId, currentSite, setCurrentSite, refetchSites, canManageSites, drawer, openAddSite, openEditSite, closeDrawer, pickerOpen, openPicker, closePicker],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite(): SiteContextValue {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite() must be used inside <SiteProvider> (mounted by AppShell)");
  return ctx;
}

// ---------------------------------------------------------------------------
// Helpers for pages that keep their own asset selection
// ---------------------------------------------------------------------------

/** The current site's id when it is one of `available`, else null. */
export function usePreferredSiteId(available: ReadonlyArray<{ id: number }>): number | null {
  const { currentSiteId } = useSite();
  return typeof currentSiteId === "number" && available.some((a) => a.id === currentSiteId) ? currentSiteId : null;
}

/**
 * Follow header switches: whenever the global site CHANGES to one of
 * `available`, call `apply(id)`. A page-local override made afterwards is left
 * alone until the next global change ("default only").
 */
export function useFollowCurrentSite(available: ReadonlyArray<{ id: number }>, apply: (id: number) => void): void {
  const { currentSiteId } = useSite();
  const applied = useRef<SiteId | null>(null);
  useEffect(() => {
    if (applied.current === currentSiteId) return;
    if (currentSiteId === "all") {
      applied.current = "all";
      return;
    }
    if (available.some((a) => a.id === currentSiteId)) {
      applied.current = currentSiteId;
      apply(currentSiteId);
    }
  }, [currentSiteId, available, apply]);
}

// ---------------------------------------------------------------------------
// Formatting shared by the switcher, the launcher and the Sites table
// ---------------------------------------------------------------------------

const fmt = (v: number) => (Number.isInteger(v) ? v : Math.round(v * 10) / 10).toLocaleString("en-IN");

/** "100 MW / 400 MWh" */
export function siteCapacity(site: { powerRatingMw: number; capacityMwh: number }): string {
  return `${fmt(site.powerRatingMw)} MW / ${fmt(site.capacityMwh)} MWh`;
}

/** Fleet totals in the same shape. */
export function fleetCapacity(sites: ReadonlyArray<{ powerRatingMw: number; capacityMwh: number }>): string {
  return siteCapacity({
    powerRatingMw: sites.reduce((s, a) => s + a.powerRatingMw, 0),
    capacityMwh: sites.reduce((s, a) => s + a.capacityMwh, 0),
  });
}
